import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Metrics = {
  progressPercent: number;
  totalLessons: number;
  completedLessons: number;
  quizAvgPct: number | null;
  homeworkTotal: number;
  homeworkSubmitted: number;
  homeworkOnTime: number;
  homeworkLate: number;
  homeworkMissing: number;
  homeworkPending: number;
};

type Props = { courseId: string };

function formatScore(score: number | null): string {
  if (score == null) return "—";
  return `${score}%`;
}

export function StudentMetricsPanel({ courseId }: Props) {
  const t = useTranslations("player");
  const tRoot = useTranslations();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch(`/v1/courses/${courseId}/metrics`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (!active) return;
        if (!res.ok) {
          // LRN-20: translated by error code (C4), not the server's English message.
          setError(apiErrorText(json, tRoot, "player.metricsLoadFailed"));
          return;
        }
        setMetrics(json.data.metrics as Metrics);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setError(t("metricsLoadFailed"));
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId, t, tRoot]);

  if (error) {
    return (
      <section data-testid="student-metrics-panel">
        <p className="text-sm text-[var(--danger)]">{error}</p>
      </section>
    );
  }

  if (!metrics) {
    return (
      <section data-testid="student-metrics-panel">
        <p className="text-sm text-[var(--text-muted)]">{t("metricsLoading")}</p>
      </section>
    );
  }

  return (
    <section
      data-testid="student-metrics-panel"
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
    >
      <h2 className="text-lg font-medium">{t("metricsTitle")}</h2>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[var(--text-muted)]">{t("metricsProgress")}</dt>
          <dd data-testid="student-metrics-progress">
            {formatScore(metrics.progressPercent)} ({metrics.completedLessons}/
            {metrics.totalLessons})
          </dd>
        </div>
        <div>
          <dt className="text-[var(--text-muted)]">{t("metricsQuizAvg")}</dt>
          <dd data-testid="student-metrics-quiz">{formatScore(metrics.quizAvgPct)}</dd>
        </div>
        <div>
          <dt className="text-[var(--text-muted)]">{t("metricsHomeworkSubmitted")}</dt>
          <dd data-testid="student-metrics-hw-submitted">
            {metrics.homeworkSubmitted}/{metrics.homeworkTotal}
          </dd>
        </div>
        <div>
          <dt className="text-[var(--text-muted)]">{t("metricsHomeworkTiming")}</dt>
          <dd data-testid="student-metrics-hw-timing">
            {t("metricsHomeworkTimingValues", {
              onTime: metrics.homeworkOnTime,
              late: metrics.homeworkLate,
              missing: metrics.homeworkMissing,
              pending: metrics.homeworkPending,
            })}
          </dd>
        </div>
      </dl>
    </section>
  );
}
